import _ from 'lodash';
import * as Constants from '../constants/redux';
import {
  DraftsOrder,
  ExternalPostsOrder,
  TopicPostsOrder,
} from '../graphql/generated/graphqlTypes';
import { DEFAULT_EMOJI_NAMES } from '../utils/emoji';
import { persistReducer } from '../utils/persistRedux';
import Sizes from '../utils/responsive/Sizes';
import isSize from '../utils/responsive/isSize';

export type SidebarSectionName =
  | 'apps'
  | 'favorites'
  | 'myTopics'
  | 'allTopics';

export type ToastLevel = 'error' | 'warning' | 'success';

type ToastRenderFunction = (props: {
  onDismiss: () => void;
}) => React.ReactNode;

interface ToastRenderProps {
  message: React.ReactNode;
  level?: ToastLevel;
}

interface Toast {
  id: string;
  render:
    | ToastRenderFunction
    // TODO: This case doesn't seem to be used, but ToastContainer handles it.
    | ToastRenderProps;
}

export interface UIState {
  descriptionCollapsedTopics: { [key: string]: boolean | undefined };
  hiddenMentionIntegrationPrompts: { [key: string]: boolean | undefined };
  isSidebarOpen: boolean;
  isOrganizationSummaryCollapsed: boolean;
  isUserProfileCollapsed: boolean;
  isStickyTOCPreferred: boolean;
  isHelpMenuOpen: boolean;
  isHelpMenuVisible: boolean;
  isPostTopicSelectOpen: boolean;
  isIntegrationBannerHidden: boolean;
  // TODO: This type is not complete, but it works for now.
  modal: {
    isOpen: boolean;
    props: Record<string, unknown> & {
      close?: () => void;
    };
    View: React.ElementType | null;
  };
  // TODO: This type is not complete, but it works for now.
  rightPanel: {
    open: boolean;
    props: Record<string, unknown>;
    View: React.ElementType | null;
  };
  toasts: Toast[];
  topicPostsOrderBy: { [key: string]: TopicPostsOrder };
  templatesOrderBy: TopicPostsOrder;
  recentlyUsedEmojis: string[];
  recentlyViewedPostIds: string[];
  emojiSkinToneModifier: string | null;
  lastCodeBlockSyntax: string | null;
  dismissedSearchConnectionPrompts: string[];
  collapsedSidebarSections: {
    [key in SidebarSectionName]?: true | false;
  };
  recentlyUsedTemplateIds: string[];
  recentlyMentionedUserIds: string[];
  externalTopbarLoginRedirectPath?: string;
  externalPostsOrder: ExternalPostsOrder;
  draftsOrder: DraftsOrder;
}

const initialState: UIState = {
  descriptionCollapsedTopics: {},
  hiddenMentionIntegrationPrompts: {},
  isSidebarOpen: !isSize(Sizes.SM),
  isOrganizationSummaryCollapsed: false,
  isUserProfileCollapsed: false,
  isStickyTOCPreferred: true,
  isHelpMenuOpen: false,
  isHelpMenuVisible: true,
  isPostTopicSelectOpen: false,
  isIntegrationBannerHidden: false,
  modal: {
    isOpen: false,
    props: {},
    View: null,
  },
  rightPanel: {
    open: false,
    props: {},
    View: null,
  },
  toasts: [],
  topicPostsOrderBy: {},
  templatesOrderBy: TopicPostsOrder.Popularity,
  recentlyUsedEmojis: DEFAULT_EMOJI_NAMES,
  recentlyViewedPostIds: [],
  emojiSkinToneModifier: null,
  lastCodeBlockSyntax: null,
  dismissedSearchConnectionPrompts: [],
  collapsedSidebarSections: {},
  recentlyUsedTemplateIds: [],
  recentlyMentionedUserIds: [],
  externalTopbarLoginRedirectPath: undefined,
  externalPostsOrder: ExternalPostsOrder.Popularity,
  draftsOrder: DraftsOrder.RecentlyViewed,
};

// TODO: export from namespaces fall within that rule, remove once namespace is changed
/* eslint-disable import/no-unused-modules */

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-namespace
namespace Actions {
  export interface UI_MODAL_CLOSE {
    type: typeof Constants.UI_MODAL_CLOSE;
    reset: boolean;
  }

  export interface UI_MODAL_OPEN {
    type: typeof Constants.UI_MODAL_OPEN;
    props: Record<string, unknown>;
    View: React.ElementType;
  }

  export interface UI_STICKY_TOC_PREFERRED_SET {
    type: typeof Constants.UI_STICKY_TOC_PREFERRED_SET;
    value: boolean;
  }

  export interface UI_RIGHT_PANEL_CLOSE {
    type: typeof Constants.UI_RIGHT_PANEL_CLOSE;
    reset: boolean;
  }

  export interface UI_RIGHT_PANEL_OPEN {
    type: typeof Constants.UI_RIGHT_PANEL_OPEN;
    props: Record<string, unknown>;
    View: React.ElementType;
  }

  export interface UI_SIDEBAR_TOGGLE {
    type: typeof Constants.UI_SIDEBAR_TOGGLE;
    forceValue: boolean | undefined;
  }

  export interface UI_SIDEBAR_TOGGLE_SECTION {
    type: typeof Constants.UI_SIDEBAR_TOGGLE_SECTION;
    section: SidebarSectionName;
  }

  export interface UI_ORGANIZATION_SUMMARY_COLLAPSED {
    type: typeof Constants.UI_ORGANIZATION_SUMMARY_COLLAPSED;
    collapsed: boolean;
  }

  export interface UI_USER_PROFILE_COLLAPSED {
    type: typeof Constants.UI_USER_PROFILE_COLLAPSED;
    collapsed: boolean;
  }

  export interface UI_TOPIC_DESCRIPTION_TOGGLE {
    type: typeof Constants.UI_TOPIC_DESCRIPTION_TOGGLE;
    id: string;
  }

  export interface UI_TOAST_CLEAR {
    type: typeof Constants.UI_TOAST_CLEAR;
  }

  export interface UI_TOAST_PUSH {
    type: typeof Constants.UI_TOAST_PUSH;
    id: string;
    render: ToastRenderFunction;
  }

  export interface UI_TOAST_REMOVE {
    type: typeof Constants.UI_TOAST_REMOVE;
    id: string;
  }

  export interface UI_TOPIC_POSTS_ORDER_BY_CHANGED {
    type: typeof Constants.UI_TOPIC_POSTS_ORDER_BY_CHANGED;
    topicId: string;
    orderBy: TopicPostsOrder;
  }

  export interface UI_TEMPLATES_ORDER_BY_CHANGED {
    type: typeof Constants.UI_TEMPLATES_ORDER_BY_CHANGED;
    orderBy: TopicPostsOrder;
  }

  export interface UI_MENTION_INTEGRATION_PROMPT_HIDE {
    type: typeof Constants.UI_MENTION_INTEGRATION_PROMPT_HIDE;
    platform: string;
  }

  export interface UI_HELP_MENU_OPEN_CHANGED {
    type: typeof Constants.UI_HELP_MENU_OPEN_CHANGED;
    open: boolean;
  }

  export interface UI_HELP_MENU_OPEN_TOGGLED {
    type: typeof Constants.UI_HELP_MENU_OPEN_TOGGLED;
  }

  export interface UI_HELP_MENU_VISIBLE_CHANGED {
    type: typeof Constants.UI_HELP_MENU_VISIBLE_CHANGED;
    visible: boolean;
  }

  export interface UI_POST_TOPIC_SELECT_OPEN_CHANGED {
    type: typeof Constants.UI_POST_TOPIC_SELECT_OPEN_CHANGED;
    open: boolean;
  }

  export interface UI_RECENTLY_USED_EMOJIS_ADDED {
    type: typeof Constants.UI_RECENTLY_USED_EMOJIS_ADDED;
    name: string;
  }

  export interface UI_RECENTLY_VIEWED_POSTS_ADDED {
    type: typeof Constants.UI_RECENTLY_VIEWED_POSTS_ADDED;
    postIds: string[];
  }

  export interface UI_RECENTLY_VIEWED_POSTS_REMOVED {
    type: typeof Constants.UI_RECENTLY_VIEWED_POSTS_REMOVED;
    postId: string;
  }

  export interface UI_EMOJI_SKIN_TONE_CHANGED {
    type: typeof Constants.UI_EMOJI_SKIN_TONE_CHANGED;
    value: string;
  }

  export interface UI_CODE_BLOCK_SYNTAX_CHANGED {
    type: typeof Constants.UI_CODE_BLOCK_SYNTAX_CHANGED;
    value: string;
  }

  export interface UI_INTEGRATION_BANNER_HIDE {
    type: typeof Constants.UI_INTEGRATION_BANNER_HIDE;
  }

  export interface UI_DISMISS_SEARCH_CONNECTION_PROMPT {
    type: typeof Constants.UI_DISMISS_SEARCH_CONNECTION_PROMPT;
    platform: string;
  }

  export interface UI_ADD_USED_TEMPLATE {
    type: typeof Constants.UI_ADD_USED_TEMPLATE;
    templateId: string;
  }

  export interface UI_ADD_MENTIONED_USER {
    type: typeof Constants.UI_ADD_MENTIONED_USER;
    userId: string;
  }

  export interface UI_SET_EXTERNAL_TOPBAR_LOGIN_REDIRECT_PATH {
    type: typeof Constants.UI_SET_EXTERNAL_TOPBAR_LOGIN_REDIRECT_PATH;
    redirectPath?: string;
  }

  export interface UI_SET_EXTERNAL_POSTS_ORDER {
    type: typeof Constants.UI_SET_EXTERNAL_POSTS_ORDER;
    orderBy: ExternalPostsOrder;
  }

  export interface UI_SET_DRAFTS_ORDER {
    type: typeof Constants.UI_SET_DRAFTS_ORDER;
    orderBy: DraftsOrder;
  }

  export interface OTHER {
    type?: undefined;
  }

  export type ANY =
    | UI_MODAL_CLOSE
    | UI_MODAL_OPEN
    | UI_STICKY_TOC_PREFERRED_SET
    | UI_RIGHT_PANEL_CLOSE
    | UI_RIGHT_PANEL_OPEN
    | UI_SIDEBAR_TOGGLE
    | UI_SIDEBAR_TOGGLE_SECTION
    | UI_ORGANIZATION_SUMMARY_COLLAPSED
    | UI_USER_PROFILE_COLLAPSED
    | UI_TOPIC_DESCRIPTION_TOGGLE
    | UI_TOAST_CLEAR
    | UI_TOAST_PUSH
    | UI_TOAST_REMOVE
    | UI_TOPIC_POSTS_ORDER_BY_CHANGED
    | UI_TEMPLATES_ORDER_BY_CHANGED
    | UI_MENTION_INTEGRATION_PROMPT_HIDE
    | UI_HELP_MENU_OPEN_CHANGED
    | UI_HELP_MENU_OPEN_TOGGLED
    | UI_HELP_MENU_VISIBLE_CHANGED
    | UI_POST_TOPIC_SELECT_OPEN_CHANGED
    | UI_RECENTLY_USED_EMOJIS_ADDED
    | UI_RECENTLY_VIEWED_POSTS_ADDED
    | UI_RECENTLY_VIEWED_POSTS_REMOVED
    | UI_EMOJI_SKIN_TONE_CHANGED
    | UI_CODE_BLOCK_SYNTAX_CHANGED
    | UI_INTEGRATION_BANNER_HIDE
    | UI_DISMISS_SEARCH_CONNECTION_PROMPT
    | UI_ADD_USED_TEMPLATE
    | UI_ADD_MENTIONED_USER
    | UI_SET_EXTERNAL_TOPBAR_LOGIN_REDIRECT_PATH
    | UI_SET_EXTERNAL_POSTS_ORDER
    | UI_SET_DRAFTS_ORDER
    | OTHER;
}

function uiReducer(state = initialState, action: Actions.ANY = {}): UIState {
  switch (action.type) {
    case Constants.UI_MODAL_CLOSE:
      if (action.reset) {
        return {
          ...state,
          modal: initialState.modal,
        };
      }
      return {
        ...state,
        modal: {
          ...state.modal,
          isOpen: false,
        },
      };
    case Constants.UI_MODAL_OPEN:
      return {
        ...state,
        modal: {
          isOpen: true,
          props: action.props,
          View: action.View,
        },
      };
    case Constants.UI_STICKY_TOC_PREFERRED_SET:
      return {
        ...state,
        isStickyTOCPreferred: action.value,
      };
    case Constants.UI_RIGHT_PANEL_CLOSE:
      if (action.reset) {
        return {
          ...state,
          rightPanel: initialState.rightPanel,
        };
      }
      return {
        ...state,
        rightPanel: {
          ...state.rightPanel,
          open: false,
        },
      };
    case Constants.UI_RIGHT_PANEL_OPEN:
      return {
        ...state,
        rightPanel: {
          open: true,
          props: action.props,
          View: action.View,
        },
      };
    case Constants.UI_SIDEBAR_TOGGLE: {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      const { forceValue } = action;
      return {
        ...state,
        isSidebarOpen:
          forceValue !== undefined ? forceValue : !state.isSidebarOpen,
      };
    }
    case Constants.UI_SIDEBAR_TOGGLE_SECTION: {
      const { section } = action;
      return {
        ...state,
        collapsedSidebarSections: {
          ...state.collapsedSidebarSections,
          [section]: !state.collapsedSidebarSections[section],
        },
      };
    }
    case Constants.UI_ORGANIZATION_SUMMARY_COLLAPSED: {
      return {
        ...state,
        isOrganizationSummaryCollapsed: action.collapsed,
      };
    }
    case Constants.UI_USER_PROFILE_COLLAPSED: {
      return {
        ...state,
        isUserProfileCollapsed: action.collapsed,
      };
    }
    case Constants.UI_TOPIC_DESCRIPTION_TOGGLE: {
      const { id } = action;
      const descriptionCollapsedTopics = {
        ...state.descriptionCollapsedTopics,
      };

      if (descriptionCollapsedTopics[id]) {
        delete descriptionCollapsedTopics[id];
      } else {
        descriptionCollapsedTopics[id] = true;
      }

      return {
        ...state,
        descriptionCollapsedTopics,
      };
    }
    case Constants.UI_TOAST_CLEAR:
      return {
        ...state,
        toasts: [],
      };
    case Constants.UI_TOAST_PUSH:
      return {
        ...state,
        toasts: [
          ...state.toasts,
          {
            id: action.id,
            render: action.render,
          },
        ],
      };
    case Constants.UI_TOAST_REMOVE:
      return {
        ...state,
        toasts: state.toasts.filter((toast) => toast.id !== action.id),
      };
    case Constants.UI_TOPIC_POSTS_ORDER_BY_CHANGED:
      return {
        ...state,
        topicPostsOrderBy: {
          ...state.topicPostsOrderBy,
          [action.topicId]: action.orderBy,
        },
      };
    case Constants.UI_TEMPLATES_ORDER_BY_CHANGED:
      return {
        ...state,
        templatesOrderBy: action.orderBy,
      };
    case Constants.UI_MENTION_INTEGRATION_PROMPT_HIDE:
      return {
        ...state,
        hiddenMentionIntegrationPrompts: {
          ...state.hiddenMentionIntegrationPrompts,
          [action.platform]: true,
        },
      };
    case Constants.UI_HELP_MENU_OPEN_CHANGED:
      return {
        ...state,
        isHelpMenuOpen: action.open,
      };
    case Constants.UI_HELP_MENU_OPEN_TOGGLED:
      return {
        ...state,
        isHelpMenuOpen: !state.isHelpMenuOpen,
      };
    case Constants.UI_HELP_MENU_VISIBLE_CHANGED:
      return {
        ...state,
        isHelpMenuVisible: action.visible,
      };
    case Constants.UI_POST_TOPIC_SELECT_OPEN_CHANGED:
      return {
        ...state,
        isPostTopicSelectOpen: action.open,
      };
    case Constants.UI_RECENTLY_USED_EMOJIS_ADDED: {
      const HISTORY_MAX_LENGTH = 100;
      return {
        ...state,
        recentlyUsedEmojis: [action.name, ...state.recentlyUsedEmojis].slice(
          0,
          HISTORY_MAX_LENGTH,
        ),
      };
    }
    case Constants.UI_RECENTLY_VIEWED_POSTS_ADDED: {
      const HISTORY_MAX_LENGTH = 25;
      return {
        ...state,
        recentlyViewedPostIds: _.uniq([
          ...action.postIds,
          ...state.recentlyViewedPostIds,
        ]).slice(0, HISTORY_MAX_LENGTH),
      };
    }

    case Constants.UI_RECENTLY_VIEWED_POSTS_REMOVED: {
      return {
        ...state,
        recentlyViewedPostIds: _.without(
          state.recentlyViewedPostIds,
          action.postId,
        ),
      };
    }

    case Constants.UI_EMOJI_SKIN_TONE_CHANGED:
      return {
        ...state,
        emojiSkinToneModifier: action.value,
      };
    case Constants.UI_CODE_BLOCK_SYNTAX_CHANGED:
      return {
        ...state,
        lastCodeBlockSyntax: action.value,
      };
    case Constants.UI_INTEGRATION_BANNER_HIDE:
      return {
        ...state,
        isIntegrationBannerHidden: true,
      };
    case Constants.UI_DISMISS_SEARCH_CONNECTION_PROMPT:
      return {
        ...state,
        dismissedSearchConnectionPrompts: [
          ...state.dismissedSearchConnectionPrompts,
          action.platform,
        ],
      };
    case Constants.UI_ADD_USED_TEMPLATE:
      return {
        ...state,
        recentlyUsedTemplateIds: _.uniq([
          action.templateId,
          ...state.recentlyUsedTemplateIds,
        ]).slice(0, 10),
      };
    case Constants.UI_ADD_MENTIONED_USER:
      return {
        ...state,
        recentlyMentionedUserIds: _.uniq([
          action.userId,
          ...state.recentlyMentionedUserIds,
        ]).slice(0, 25),
      };
    case Constants.UI_SET_EXTERNAL_TOPBAR_LOGIN_REDIRECT_PATH:
      return {
        ...state,
        externalTopbarLoginRedirectPath: action.redirectPath,
      };
    case Constants.UI_SET_EXTERNAL_POSTS_ORDER:
      return {
        ...state,
        externalPostsOrder: action.orderBy,
      };
    case Constants.UI_SET_DRAFTS_ORDER:
      return {
        ...state,
        draftsOrder: action.orderBy,
      };
    default:
      return state;
  }
}

export default persistReducer(
  {
    key: 'ui',
    whitelist: [
      'descriptionCollapsedTopics',
      'hiddenMentionIntegrationPrompts',
      'isStickyTOCPreferred',
      'isSidebarOpen',
      'isOrganizationSummaryCollapsed',
      'isUserProfileCollapsed',
      'topicPostsOrderBy',
      'templatesOrderBy',
      'recentlyUsedEmojis',
      'recentlyViewedPostIds',
      'emojiSkinToneModifier',
      'lastCodeBlockSyntax',
      'dismissedSearchConnectionPrompts',
      'collapsedSidebarSections',
      'recentlyUsedTemplateIds',
      'recentlyMentionedUserIds',
      'externalPostsOrder',
      'draftsOrder',
    ],
    // @ts-expect-error
    migrate: (state: UIState) =>
      Promise.resolve(
        state &&
          (state.recentlyUsedEmojis?.length
            ? state
            : {
                ...state,
                recentlyUsedEmojis: DEFAULT_EMOJI_NAMES,
              }),
      ),
  },
  uiReducer,
);
